import { useMemo } from 'react';
import type {
  ChartWidget,
  DatasetBinding,
  KpiWidget,
  PieWidget,
  TableWidget,
  TextWidget,
  Widget,
  WidgetAggregation,
  WidgetNumberFormat,
} from '../../../api/dashboards.js';
import type { DatasetSummary } from '../../../api/types.js';
import { useDrillTargets } from '../../../api/drill-targets.js';

/**
 * Right-rail config panel for the selected widget. Deep form that
 * drives all widget options — field bindings, aggregations, formatting,
 * drill-through.
 */
export function WidgetConfigPanel({
  widget,
  datasets,
  datasetCatalog,
  onChange,
  onDelete,
}: {
  widget: Widget;
  datasets: DatasetBinding[];
  datasetCatalog: DatasetSummary[] | undefined;
  onChange(next: Widget): void;
  onDelete(): void;
}): JSX.Element {
  const bound = datasets.find((d) => d.key === widget.binding?.datasetKey);
  const cols = useMemo(() => fieldsForBinding(bound, datasetCatalog), [bound, datasetCatalog]);

  return (
    <aside className="widget-config">
      <header className="widget-config-head">
        <input
          className="input input--sm"
          value={widget.title}
          onChange={(e) => onChange({ ...widget, title: e.target.value })}
          placeholder="Widget title"
        />
        <button
          type="button"
          className="btn-icon"
          onClick={onDelete}
          title="Delete widget"
          aria-label="Delete widget"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.75"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M3 5h10M6 5V3h4v2M5 5l1 9h4l1-9" />
          </svg>
        </button>
      </header>

      <div className="widget-config-body">
        {widget.type !== 'text' && (
          <BindingSection widget={widget} datasets={datasets} onChange={onChange} />
        )}
        {widget.type === 'kpi' && <KpiConfig widget={widget} cols={cols} onChange={onChange} />}
        {(widget.type === 'bar' || widget.type === 'line' || widget.type === 'area') && (
          <ChartConfig widget={widget} cols={cols} onChange={onChange} />
        )}
        {(widget.type === 'pie' || widget.type === 'donut') && (
          <PieConfig widget={widget} cols={cols} onChange={onChange} />
        )}
        {widget.type === 'table' && <TableConfig widget={widget} cols={cols} onChange={onChange} />}
        {widget.type === 'text' && <TextConfig widget={widget} onChange={onChange} />}

        <DrillSection widget={widget} cols={cols} onChange={onChange} />
      </div>
    </aside>
  );
}

function fieldsForBinding(
  bound: DatasetBinding | undefined,
  catalog: DatasetSummary[] | undefined,
): string[] {
  if (!bound) return [];
  if (bound.columns && bound.columns.length > 0) return bound.columns;
  const entry = catalog?.find((d) => d.name === bound.datasetName);
  return entry?.columns.map((c) => c.name) ?? [];
}

function BindingSection({
  widget,
  datasets,
  onChange,
}: {
  widget: Widget;
  datasets: DatasetBinding[];
  onChange(next: Widget): void;
}): JSX.Element {
  return (
    <section className="widget-config-section">
      <h4>Data</h4>
      <label className="field">
        <span className="field-label">Dataset binding</span>
        <select
          className="select"
          value={widget.binding?.datasetKey ?? ''}
          onChange={(e) =>
            onChange({
              ...widget,
              binding: { datasetKey: e.target.value },
            } as Widget)
          }
        >
          <option value="">(select)</option>
          {datasets.map((d) => (
            <option key={d.key} value={d.key}>
              {d.key} · {d.datasetName}
            </option>
          ))}
        </select>
      </label>
    </section>
  );
}

export function KpiConfig({
  widget,
  cols,
  onChange,
}: {
  widget: KpiWidget;
  cols: string[];
  onChange(next: Widget): void;
}): JSX.Element {
  const c = widget.config;
  const patch = (p: Partial<KpiWidget['config']>) =>
    onChange({ ...widget, config: { ...c, ...p } } as KpiWidget);
  return (
    <section className="widget-config-section">
      <h4>Value</h4>
      <label className="field">
        <span className="field-label">Field</span>
        <FieldPicker cols={cols} value={c.valueField} onChange={(v) => patch({ valueField: v })} />
      </label>
      <label className="field">
        <span className="field-label">Aggregation</span>
        <AggSelect value={c.valueAgg ?? 'sum'} onChange={(v) => patch({ valueAgg: v })} />
      </label>
      {/* CSAGE-520 — ratio: value / denominator over the same rows. */}
      <label className="field">
        <span className="field-label">Divide by (optional)</span>
        <FieldPicker
          cols={cols}
          value={c.denominatorField ?? ''}
          onChange={(v) => {
            const nextConfig = { ...c };
            if (v) {
              nextConfig.denominatorField = v;
            } else {
              // The API rejects denominatorAgg without denominatorField.
              delete nextConfig.denominatorField;
              delete nextConfig.denominatorAgg;
            }
            onChange({ ...widget, config: nextConfig } as KpiWidget);
          }}
          allowEmpty
        />
      </label>
      {c.denominatorField && (
        <label className="field">
          <span className="field-label">Divide by aggregation</span>
          <AggSelect
            value={c.denominatorAgg ?? 'sum'}
            onChange={(v) => patch({ denominatorAgg: v })}
          />
        </label>
      )}
      <label className="field">
        <span className="field-label">Format</span>
        <FormatSelect value={c.format ?? 'number'} onChange={(v) => patch({ format: v })} />
      </label>
      <label className="field">
        <span className="field-label">Unit (optional)</span>
        <input
          className="input input--sm"
          value={c.unit ?? ''}
          onChange={(e) => patch({ unit: e.target.value })}
          placeholder="ms, req, $"
        />
      </label>
      <h4 className="widget-config-sub">Comparison (optional)</h4>
      <label className="field">
        <span className="field-label">Comparison field</span>
        <FieldPicker
          cols={cols}
          value={c.comparisonField ?? ''}
          onChange={(v) => {
            const nextConfig = { ...c };
            if (v) nextConfig.comparisonField = v;
            else delete nextConfig.comparisonField;
            onChange({ ...widget, config: nextConfig } as KpiWidget);
          }}
          allowEmpty
        />
      </label>
      {c.comparisonField && (
        <>
          <label className="field">
            <span className="field-label">Comparison aggregation</span>
            <AggSelect
              value={c.comparisonAgg ?? 'sum'}
              onChange={(v) => patch({ comparisonAgg: v })}
            />
          </label>
          <label className="field">
            <span className="field-label">Label</span>
            <input
              className="input input--sm"
              value={c.comparisonLabel ?? ''}
              onChange={(e) => patch({ comparisonLabel: e.target.value })}
              placeholder="vs last week"
            />
          </label>
          <label className="field">
            <span className="field-label">Delta direction</span>
            <select
              className="select"
              value={c.deltaDirection ?? 'positive-up'}
              onChange={(e) =>
                patch({
                  deltaDirection: e.target.value as 'positive-up' | 'negative-up' | 'neutral',
                })
              }
            >
              <option value="positive-up">Positive is good (green up)</option>
              <option value="negative-up">Positive is bad (red up)</option>
              <option value="neutral">Neutral</option>
            </select>
          </label>
        </>
      )}
    </section>
  );
}

function ChartConfig({
  widget,
  cols,
  onChange,
}: {
  widget: ChartWidget;
  cols: string[];
  onChange(next: Widget): void;
}): JSX.Element {
  const c = widget.config;
  const patch = (p: Partial<ChartWidget['config']>) =>
    onChange({ ...widget, config: { ...c, ...p } } as ChartWidget);
  return (
    <section className="widget-config-section">
      <h4>Axes</h4>
      <label className="field">
        <span className="field-label">X axis (category)</span>
        <FieldPicker cols={cols} value={c.xField} onChange={(v) => patch({ xField: v })} />
      </label>
      <label className="field">
        <span className="field-label">Y axis (value)</span>
        <FieldPicker cols={cols} value={c.yField} onChange={(v) => patch({ yField: v })} />
      </label>
      <label className="field">
        <span className="field-label">Y aggregation</span>
        <AggSelect value={c.yAgg ?? 'sum'} onChange={(v) => patch({ yAgg: v })} />
      </label>
      <label className="field">
        <span className="field-label">Y format</span>
        <FormatSelect value={c.yFormat ?? 'number'} onChange={(v) => patch({ yFormat: v })} />
      </label>
      <label className="field">
        <span className="field-label">Series (optional, splits into multiple lines)</span>
        <FieldPicker
          cols={cols}
          value={c.seriesField ?? ''}
          onChange={(v) => {
            const nextConfig = { ...c };
            if (v) nextConfig.seriesField = v;
            else delete nextConfig.seriesField;
            onChange({ ...widget, config: nextConfig } as ChartWidget);
          }}
          allowEmpty
        />
      </label>
      {widget.type === 'bar' && (
        <>
          <label className="field">
            <span className="field-label">Orientation</span>
            <select
              className="select"
              value={c.orientation ?? 'vertical'}
              onChange={(e) => patch({ orientation: e.target.value as 'vertical' | 'horizontal' })}
            >
              <option value="vertical">Vertical</option>
              <option value="horizontal">Horizontal</option>
            </select>
          </label>
          <Checkbox
            label="Stacked"
            checked={c.stacked ?? false}
            onChange={(v) => patch({ stacked: v })}
          />
        </>
      )}
      <Checkbox
        label="Show legend"
        checked={c.showLegend ?? true}
        onChange={(v) => patch({ showLegend: v })}
      />
      <Checkbox
        label="Show grid"
        checked={c.showGrid ?? true}
        onChange={(v) => patch({ showGrid: v })}
      />
    </section>
  );
}

function PieConfig({
  widget,
  cols,
  onChange,
}: {
  widget: PieWidget;
  cols: string[];
  onChange(next: Widget): void;
}): JSX.Element {
  const c = widget.config;
  const patch = (p: Partial<PieWidget['config']>) =>
    onChange({ ...widget, config: { ...c, ...p } } as PieWidget);
  return (
    <section className="widget-config-section">
      <h4>Slices</h4>
      <label className="field">
        <span className="field-label">Category field</span>
        <FieldPicker
          cols={cols}
          value={c.categoryField}
          onChange={(v) => patch({ categoryField: v })}
        />
      </label>
      <label className="field">
        <span className="field-label">Value field</span>
        <FieldPicker cols={cols} value={c.valueField} onChange={(v) => patch({ valueField: v })} />
      </label>
      <label className="field">
        <span className="field-label">Aggregation</span>
        <AggSelect value={c.valueAgg ?? 'sum'} onChange={(v) => patch({ valueAgg: v })} />
      </label>
      <label className="field">
        <span className="field-label">Max slices (rest grouped as "Other")</span>
        <input
          type="number"
          min={2}
          max={24}
          className="input input--sm"
          value={c.maxSlices ?? 8}
          onChange={(e) => patch({ maxSlices: Number(e.target.value) })}
        />
      </label>
      <Checkbox
        label="Show legend"
        checked={c.showLegend ?? true}
        onChange={(v) => patch({ showLegend: v })}
      />
      <Checkbox
        label="Show labels"
        checked={c.showLabels ?? false}
        onChange={(v) => patch({ showLabels: v })}
      />
    </section>
  );
}

function TableConfig({
  widget,
  cols,
  onChange,
}: {
  widget: TableWidget;
  cols: string[];
  onChange(next: Widget): void;
}): JSX.Element {
  const c = widget.config;
  const patch = (p: Partial<TableWidget['config']>) =>
    onChange({ ...widget, config: { ...c, ...p } } as TableWidget);
  const addColumn = () => {
    const unused = cols.find((col) => !c.columns.some((cc) => cc.field === col)) ?? cols[0];
    if (!unused) return;
    patch({
      columns: [...c.columns, { field: unused, label: humanize(unused) }],
    });
  };
  return (
    <section className="widget-config-section">
      <h4>Columns</h4>
      {c.columns.length === 0 && (
        <p className="widget-config-empty">No columns yet. Add a field to start.</p>
      )}
      {c.columns.map((col, i) => (
        <div className="widget-config-col" key={`${col.field}-${i}`}>
          <FieldPicker
            cols={cols}
            value={col.field}
            onChange={(v) => {
              const next = [...c.columns];
              next[i] = { ...col, field: v };
              patch({ columns: next });
            }}
          />
          <input
            className="input input--sm"
            value={col.label}
            onChange={(e) => {
              const next = [...c.columns];
              next[i] = { ...col, label: e.target.value };
              patch({ columns: next });
            }}
            placeholder="Label"
          />
          <FormatSelect
            value={col.format ?? 'number'}
            onChange={(v) => {
              const next = [...c.columns];
              next[i] = { ...col, format: v };
              patch({ columns: next });
            }}
          />
          <button
            type="button"
            className="btn-icon"
            onClick={() => patch({ columns: c.columns.filter((_, j) => j !== i) })}
            aria-label="Remove column"
          >
            ×
          </button>
        </div>
      ))}
      <button
        type="button"
        className="btn btn--ghost btn--sm"
        onClick={addColumn}
        disabled={cols.length === 0}
      >
        + Add column
      </button>

      <label className="field">
        <span className="field-label">Row limit</span>
        <input
          type="number"
          min={1}
          max={5000}
          className="input input--sm"
          value={c.rowLimit ?? 100}
          onChange={(e) => patch({ rowLimit: Number(e.target.value) })}
        />
      </label>
      <Checkbox
        label="Zebra rows"
        checked={c.zebra ?? true}
        onChange={(v) => patch({ zebra: v })}
      />
      <Checkbox
        label="Dense spacing"
        checked={c.dense ?? false}
        onChange={(v) => patch({ dense: v })}
      />
    </section>
  );
}

function TextConfig({
  widget,
  onChange,
}: {
  widget: TextWidget;
  onChange(next: Widget): void;
}): JSX.Element {
  const c = widget.config;
  const patch = (p: Partial<TextWidget['config']>) =>
    onChange({ ...widget, config: { ...c, ...p } } as TextWidget);
  return (
    <section className="widget-config-section">
      <h4>Text content</h4>
      <textarea
        className="textarea"
        rows={8}
        value={c.markdown}
        onChange={(e) => patch({ markdown: e.target.value })}
        placeholder="**Bold**, *italic*, `code`, [link](https://...), or * bullets"
      />
      <label className="field">
        <span className="field-label">Align</span>
        <select
          className="select"
          value={c.align ?? 'left'}
          onChange={(e) => patch({ align: e.target.value as 'left' | 'center' | 'right' })}
        >
          <option value="left">Left</option>
          <option value="center">Center</option>
          <option value="right">Right</option>
        </select>
      </label>
      <label className="field">
        <span className="field-label">Size</span>
        <select
          className="select"
          value={c.size ?? 'md'}
          onChange={(e) => patch({ size: e.target.value as 'sm' | 'md' | 'lg' })}
        >
          <option value="sm">Small</option>
          <option value="md">Medium</option>
          <option value="lg">Large</option>
        </select>
      </label>
    </section>
  );
}

function DrillSection({
  widget,
  cols,
  onChange,
}: {
  widget: Widget;
  cols: string[];
  onChange(next: Widget): void;
}): JSX.Element {
  const { data: targets } = useDrillTargets();
  const patchDrill = (next: Widget['drill'] | undefined) => {
    const copy = { ...widget } as Widget;
    if (next) (copy as { drill?: Widget['drill'] }).drill = next;
    else delete (copy as { drill?: Widget['drill'] }).drill;
    onChange(copy);
  };
  const drill = widget.drill;

  return (
    <section className="widget-config-section">
      <h4>Drill-through</h4>
      <label className="field">
        <span className="field-label">Target</span>
        <select
          className="select"
          value={drill?.drillTargetId ?? ''}
          onChange={(e) => {
            const v = e.target.value;
            if (!v) return patchDrill(undefined);
            patchDrill({
              drillTargetId: v,
              paramMap: drill?.paramMap ?? {},
              mode: drill?.mode ?? 'row',
            });
          }}
        >
          <option value="">(none)</option>
          {targets?.map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} ({t.targetKind})
            </option>
          ))}
        </select>
      </label>
      {drill && (
        <>
          <label className="field">
            <span className="field-label">Click mode</span>
            <select
              className="select"
              value={drill.mode ?? 'row'}
              onChange={(e) => patchDrill({ ...drill, mode: e.target.value as 'widget' | 'row' })}
            >
              <option value="row">Per row (table/chart cell)</option>
              <option value="widget">Whole widget</option>
            </select>
          </label>
          <DrillParamMap
            drill={drill}
            targetId={drill.drillTargetId}
            cols={cols}
            onChange={patchDrill}
          />
        </>
      )}
    </section>
  );
}

function DrillParamMap({
  drill,
  targetId,
  cols,
  onChange,
}: {
  drill: NonNullable<Widget['drill']>;
  targetId: string;
  cols: string[];
  onChange(next: NonNullable<Widget['drill']>): void;
}): JSX.Element {
  const { data: targets } = useDrillTargets();
  const target = targets?.find((t) => t.id === targetId);
  if (!target) return <></>;
  return (
    <div className="widget-config-map">
      {target.paramContract.map((p) => (
        <div key={p.name} className="widget-config-map-row">
          <label className="field-label">
            {p.name}
            {p.required && <span className="required">*</span>}
          </label>
          <select
            className="select"
            value={drill.paramMap?.[p.name] ?? ''}
            onChange={(e) => {
              const next = { ...(drill.paramMap ?? {}) };
              if (!e.target.value) delete next[p.name];
              else next[p.name] = e.target.value;
              onChange({ ...drill, paramMap: next });
            }}
          >
            <option value="">(unmapped)</option>
            {cols.map((c) => (
              <option key={c} value={`row.${c}`}>
                row.{c}
              </option>
            ))}
          </select>
        </div>
      ))}
    </div>
  );
}

// ─── Small shared controls ────────────────────────────────────────────────

function FieldPicker({
  cols,
  value,
  onChange,
  allowEmpty,
}: {
  cols: string[];
  value: string;
  onChange(v: string): void;
  allowEmpty?: boolean;
}): JSX.Element {
  // Free-text fields are error-prone (typos silently cascade to every
  // render). Always render a <select>. When we have no known columns,
  // show a disabled select with an instructive message so the author
  // knows they need to fix the binding rather than guess a field name.
  const hasCols = cols.length > 0;
  // If the stored value isn't in cols (e.g. dataset columns changed
  // after the widget was saved), surface it as a disabled option so the
  // author can see it needs re-mapping.
  const valueMissing = hasCols && value && !cols.includes(value);

  if (!hasCols) {
    return (
      <div className="field-picker-empty">
        <select className="select" disabled value="">
          <option value="">(no fields available — edit the dataset binding first)</option>
        </select>
      </div>
    );
  }

  return (
    <select className="select" value={value} onChange={(e) => onChange(e.target.value)}>
      {allowEmpty && <option value="">(none)</option>}
      {!value && !allowEmpty && <option value="">(select a field)</option>}
      {valueMissing && (
        <option value={value} disabled>
          ⚠ {value} (not in dataset)
        </option>
      )}
      {cols.map((c) => (
        <option key={c} value={c}>
          {c}
        </option>
      ))}
    </select>
  );
}

function AggSelect({
  value,
  onChange,
}: {
  value: WidgetAggregation;
  onChange(v: WidgetAggregation): void;
}): JSX.Element {
  return (
    <select
      className="select"
      value={value}
      onChange={(e) => onChange(e.target.value as WidgetAggregation)}
    >
      <option value="sum">Sum</option>
      <option value="avg">Average</option>
      <option value="count">Count rows</option>
      <option value="count_distinct">Count distinct</option>
      <option value="min">Min</option>
      <option value="max">Max</option>
      <option value="first">First</option>
      <option value="last">Last</option>
    </select>
  );
}

function FormatSelect({
  value,
  onChange,
}: {
  value: WidgetNumberFormat;
  onChange(v: WidgetNumberFormat): void;
}): JSX.Element {
  return (
    <select
      className="select"
      value={value}
      onChange={(e) => onChange(e.target.value as WidgetNumberFormat)}
    >
      <option value="number">Number</option>
      <option value="integer">Integer</option>
      <option value="decimal_1">Decimal (1)</option>
      <option value="decimal_2">Decimal (2)</option>
      <option value="percent">Percent (1 dp)</option>
      <option value="percent_0">Percent (0 dp)</option>
      <option value="currency">Currency (USD)</option>
      <option value="compact">Compact (1.2K)</option>
      <option value="bytes">Bytes</option>
      <option value="duration_ms">Duration (ms)</option>
    </select>
  );
}

function Checkbox({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange(v: boolean): void;
}): JSX.Element {
  return (
    <label className="field-checkbox">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

function humanize(s: string): string {
  return s
    .replace(/[._]/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0]!.toUpperCase() + w.slice(1))
    .join(' ');
}
